/**
 * Pipeline Report filter state + matching logic — shared between the table
 * (which applies it to the fetched rows) and PipelineReportFilterBar (which
 * renders the controls). Lives here rather than in PipelineReportTable.tsx so
 * the filter bar can import the type without an import cycle back into the
 * table module.
 */

import type { PipelineReportRow } from "@/hooks/use-pipeline-report";
import type { SelectOption } from "@/lib/option-search";
import { FILTER_ALL, FILTER_NONE } from "./filter-controls";

export interface FilterState {
  account: string;
  deal: string;
  stage: string;
  resourceId: string;
  resourcingStatus: string;
  resourcePlanReceived: string; // "" (all) | "yes" | "no"
  hoursMin: string;
  hoursMax: string;
  notes: string;
  ownerId: string;
  platformId: string;
  totalMin: string;
  totalMax: string;
  /** Plain text-contains filter — Lead Source is a free-text field, not a pick list. */
  leadSource: string;
  commitFrom: string;
  commitTo: string;
  lastActivityFrom: string;
  lastActivityTo: string;
  addedFrom: string;
  addedTo: string;
}

export const EMPTY_FILTERS: FilterState = {
  account: "",
  deal: "",
  stage: "",
  resourceId: FILTER_ALL,
  resourcingStatus: FILTER_ALL,
  resourcePlanReceived: FILTER_ALL,
  hoursMin: "",
  hoursMax: "",
  notes: "",
  ownerId: FILTER_ALL,
  platformId: FILTER_ALL,
  totalMin: "",
  totalMax: "",
  leadSource: "",
  commitFrom: "",
  commitTo: "",
  lastActivityFrom: "",
  lastActivityTo: "",
  addedFrom: "",
  addedTo: "",
};

/** Every field that differs from its "no filter" default counts once — matches the
 * pattern in src/lib/resource-filters.ts's activeFilterCount. */
export function activeFilterCount(f: FilterState): number {
  return (Object.keys(EMPTY_FILTERS) as (keyof FilterState)[]).filter(
    (k) => f[k] !== EMPTY_FILTERS[k],
  ).length;
}

function matchesText(filterValue: string, haystack: string | null): boolean {
  const needle = filterValue.trim().toLowerCase();
  if (needle === "") return true;
  return (haystack ?? "").toLowerCase().includes(needle);
}

function matchesPick(filterValue: string, rowId: string | null): boolean {
  if (filterValue === FILTER_ALL) return true;
  if (filterValue === FILTER_NONE) return rowId === null;
  return rowId === filterValue;
}

function inNumberRange(value: number, minStr: string, maxStr: string): boolean {
  if (minStr.trim() !== "") {
    const min = Number(minStr);
    if (!Number.isNaN(min) && value < min) return false;
  }
  if (maxStr.trim() !== "") {
    const max = Number(maxStr);
    if (!Number.isNaN(max) && value > max) return false;
  }
  return true;
}

// `total` is a nullable `numeric` string. `Number()` here is filter-comparison ONLY — the
// value is never written back or used in arithmetic, so the float-precision discipline in
// rate-card.ts (integer cents/micros) doesn't apply to this UI-only range check.
function inMoneyRange(value: string | null, minStr: string, maxStr: string): boolean {
  const hasMin = minStr.trim() !== "";
  const hasMax = maxStr.trim() !== "";
  if (!hasMin && !hasMax) return true;
  if (value === null) return false;
  const num = Number(value);
  if (hasMin) {
    const min = Number(minStr);
    if (!Number.isNaN(min) && num < min) return false;
  }
  if (hasMax) {
    const max = Number(maxStr);
    if (!Number.isNaN(max) && num > max) return false;
  }
  return true;
}

// `fromStr`/`toStr` come straight from `<input type="date">`, always "YYYY-MM-DD" — that
// sorts correctly against the leading 10 characters of either a date-only `commitDate` or
// a full ISO timestamp (`lastActivityAt`/`addedAt`) via plain string comparison.
function inDateRange(value: string | null, fromStr: string, toStr: string): boolean {
  const hasFrom = fromStr.trim() !== "";
  const hasTo = toStr.trim() !== "";
  if (!hasFrom && !hasTo) return true;
  if (value === null) return false;
  const datePart = value.slice(0, 10);
  if (hasFrom && datePart < fromStr) return false;
  if (hasTo && datePart > toStr) return false;
  return true;
}

export function filterRows(rows: PipelineReportRow[], f: FilterState): PipelineReportRow[] {
  return rows.filter((r) => {
    if (!matchesText(f.account, r.accountName)) return false;
    if (!matchesText(f.deal, r.deal)) return false;
    if (!matchesText(f.stage, r.stage)) return false;
    if (!matchesText(f.notes, r.notes)) return false;
    if (!matchesPick(f.resourceId, r.resourceId)) return false;
    if (!matchesPick(f.ownerId, r.ownerUserId)) return false;
    if (!matchesPick(f.platformId, r.platformConstraintId)) return false;
    if (!matchesText(f.leadSource, r.leadSource)) return false;
    if (f.resourcingStatus !== FILTER_ALL && r.resourcingStatus !== f.resourcingStatus)
      return false;
    if (f.resourcePlanReceived === "yes" && !r.resourcePlanReceived) return false;
    if (f.resourcePlanReceived === "no" && r.resourcePlanReceived) return false;
    if (!inNumberRange(r.hoursLogged, f.hoursMin, f.hoursMax)) return false;
    if (!inMoneyRange(r.total, f.totalMin, f.totalMax)) return false;
    if (!inDateRange(r.commitDate, f.commitFrom, f.commitTo)) return false;
    if (!inDateRange(r.lastActivityAt, f.lastActivityFrom, f.lastActivityTo)) return false;
    if (!inDateRange(r.addedAt, f.addedFrom, f.addedTo)) return false;
    return true;
  });
}

/** Distinct (id, label) pairs actually present in the fetched dataset, alphabetized — the
 * source for every pick-list filter dropdown. Never a separate API call. */
export function distinctOptions(
  rows: PipelineReportRow[],
  getId: (r: PipelineReportRow) => string | null,
  getLabel: (r: PipelineReportRow) => string | null,
  collator: Intl.Collator,
): SelectOption[] {
  const byId = new Map<string, string>();
  for (const r of rows) {
    const id = getId(r);
    const label = getLabel(r);
    if (id !== null && label !== null && !byId.has(id)) byId.set(id, label);
  }
  return Array.from(byId, ([value, label]) => ({ value, label })).sort((a, b) =>
    collator.compare(a.label, b.label),
  );
}
